import { PointCloudObject } from "@/object-cache";
import { applyTransformToGroup } from "@/registration-tools/common/rendering/positioned-point-cloud";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { blue } from "@faro-lotv/flat-ui";
import { LodTreeNode } from "@faro-lotv/lotv";
import { useFrame } from "@react-three/fiber";
import { useMemo, useState, type JSX } from "react";
import {
  BoxGeometry,
  Color,
  ColorRepresentation,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  Material,
  Matrix4,
} from "three";

export enum NodeType {
  /** All nodes which are currently being rendered. */
  rendered = "rendered",
  /** All nodes which are determined to be visible and relevant. */
  visible = "visible",
  /** All nodes currently stored in memory. */
  memory = "memory",
}

/** How to handle the LOD structure of the tree. */
export enum LodMode {
  /**
   * Only draw leaf nodes of the LOD structure.
   * Results in less overdraw.
   */
  leaves = "leaves",
  /** Draw the full LOD hierarchy. */
  all = "all",
}

type PointCloudWireframeProps = {
  /** The point cloud object to visualize the LOD structure of. */
  pointCloudObject: PointCloudObject;

  /** The position to apply to the wireframe. */
  transform: Matrix4;

  /** The color to use for the wireframe. */
  color?: ColorRepresentation;

  /** Which type of nodes should be visualized. */
  nodeType?: NodeType;

  /** How to handle the LOD structure of the tree.  */
  lodMode?: LodMode;

  /** Whether the wireframe should be rendered. */
  isVisible?: boolean;
};

/**
 * @returns Visualization of the LOD structure of a point cloud using wire frames.
 */
export function PointCloudWireframe({
  isVisible = true,
  ...props
}: PointCloudWireframeProps): JSX.Element | null {
  if (!isVisible) return null;

  return <PointCloudWireframeInner {...props} />;
}

/**
 * @returns the actual wireframe visualization of the point cloud.
 * Contains the expensive logic per frame, so should only be mounted when needed.
 */
function PointCloudWireframeInner({
  pointCloudObject,
  transform,
  color = blue[500],
  nodeType = NodeType.rendered,
  lodMode = LodMode.leaves,
}: Omit<PointCloudWireframeProps, "isVisible">): JSX.Element {
  const lineMaterial = useMemo(
    () =>
      new LineBasicMaterial({
        color: new Color(color).getHex(),
        linewidth: 0.5,
        transparent: true,
        opacity: 0.2,
      }),
    [color],
  );

  const [nodes, setNodes] = useState<LodTreeNode[]>([]);
  const [groupRef, setGroupRef] = useState<Group | null>(null);

  // Apply the transform to the group
  useNonExhaustiveEffect(() => {
    if (groupRef) {
      // Need to consider both the offset of the IElement as set in the Project API
      // and the internal offset in the point cloud
      const matrix = new Matrix4()
        .copy(transform)
        .multiply(pointCloudObject.tree.worldMatrix);

      applyTransformToGroup(groupRef, matrix);
    }
  }, [groupRef, pointCloudObject.matrixWorld]);

  // Determine which nodes to visualize each frame
  useFrame(() => {
    let nodesToVisualize: number[];

    switch (nodeType) {
      case NodeType.rendered:
        nodesToVisualize = [...pointCloudObject.nodesInGPU.keys()];
        break;
      case NodeType.visible:
        nodesToVisualize = pointCloudObject
          .getVisibleNodes()
          .map((node) => node.id);
        break;
      case NodeType.memory:
        nodesToVisualize = [...pointCloudObject.nodesInMemory.keys()];
        break;
    }

    const newNodes = [];
    for (const nodeId of nodesToVisualize) {
      const treeNode = pointCloudObject.tree.getNode(nodeId);
      if (
        lodMode === LodMode.leaves &&
        treeNode.children.some((child) => nodesToVisualize.includes(child.id))
      ) {
        // Only include leaf nodes to avoid overdraw
        continue;
      }

      newNodes.push(pointCloudObject.tree.getNode(nodeId));
    }
    setNodes(newNodes);
  });

  return (
    <group ref={setGroupRef}>
      {nodes.map((node) => (
        <LodNodeWireframe
          key={node.id}
          node={node}
          lineMaterial={lineMaterial}
        />
      ))}
    </group>
  );
}

type LodNodeWireframeProps = {
  /** The LOD node to display the wireframe of. */
  node: LodTreeNode;

  /** The material to use for the wireframe lines. */
  lineMaterial: Material;
};

/** @returns Debug visualization of a single LOD node. */
function LodNodeWireframe({
  node,
  lineMaterial,
}: LodNodeWireframeProps): JSX.Element {
  const boxGeometry = useMemo(() => {
    const { boundingBox } = node;
    const geometry = new BoxGeometry(
      boundingBox.max.x - boundingBox.min.x,
      boundingBox.max.y - boundingBox.min.y,
      boundingBox.max.z - boundingBox.min.z,
    );
    geometry.translate(
      (boundingBox.max.x + boundingBox.min.x) / 2,
      (boundingBox.max.y + boundingBox.min.y) / 2,
      (boundingBox.max.z + boundingBox.min.z) / 2,
    );
    return geometry;
  }, [node]);
  const lineGeometry = useMemo(
    () => new EdgesGeometry(boxGeometry),
    [boxGeometry],
  );

  return (
    <lineSegments geometry={lineGeometry}>
      <primitive object={lineMaterial} />
    </lineSegments>
  );
}
